<template>
  <Dice
    v-if="!constant"
    class="stat-icon rollable"
    :class="{ condensed: condensed }"
    :dice="resolvedDice"
    content
    :label="label"
    noexpr
  >
    <div v-if="icon" class="icon-wrapper">
      <Icon :name="icon" class="size-2x"></Icon>
      <span v-if="iconText" class="icon-text">{{ iconText }}</span>
    </div>
    <div class="name">{{ name }}</div>
    <div v-if="resolvedValue" class="value">{{ resolvedValue }}</div>
  </Dice>
  <div v-else class="stat-icon" :class="{ condensed: condensed }">
    <div v-if="icon" class="icon-wrapper">
      <Icon :name="icon" class="size-2x"></Icon>
    </div>
    <div class="name">{{ name }}</div>
    <div v-if="resolvedValue" class="value">
      {{ resolvedValue }}<span v-if="unit" class="unit">{{ unit }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { joinDice, resolveDice, type DiceExpr } from '~/assets/dice/diceEval'

const props = defineProps<{
  name: string
  value: DiceExpr
  icon?: string
  iconText?: string | number
  constant?: boolean
  unit?: string
  label?: string
  condensed?: boolean
  forceModifier?: boolean
}>()

const mods = useMods()

const resolvedValue = computed(() => {
  if (!props.value) return null
  const value = resolveDice(props.value, mods(), true)
  if (props.unit || value < 0) return `${value}`
  return `+${value}`
})

const resolvedDice = computed(() => {
  if (!props.value) return ''
  if (props.forceModifier) return joinDice('d20', props.value)
  return props.value
})
</script>

<style lang="postcss">
.stat-icon {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0.25em;
  line-height: 1.2;
  user-select: none;

  &.condensed {
    font-size: 0.8em;
  }

  @media (max-width: 600px) {
    font-size: 0.9em;
  }

  .icon-wrapper {
    position: relative;
    color: var(--primary);

    .icon {
      height: 1em;
    }

    .icon-text {
      position: absolute;
      inset: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: var(--font-stack-title);
      color: var(--text-highlight);
      text-shadow: 0 0 4px var(--background), 0 0 2px var(--background), 0 0 1px var(--background);
      text-align: center;
    }
  }

  .name {
    font-weight: 600;
    margin-top: 0.25em;
    text-transform: uppercase;
  }

  .value {
    font-size: 1.5em;
    color: var(--text-highlight);
    font-family: var(--font-stack-title);

    .unit {
      font-size: 0.5em;
    }
  }

  &.rollable {
    cursor: pointer;

    &:hover {
      background-color: var(--bg-primary);

      .value {
        color: var(--text-highlight);
      }
    }
  }
}
</style>
